import { Box} from '@mui/material';
import { useEffect, useState } from 'react';
import { createFilterModel, createOrderModel, createPaginationModel, eliminateOjectNotTruthyValues } from '../../../lib/helperFuncs';
import FilterComponentV2 from '../FilterComponent/FilterComponentv2';
import TableGrid from '../TableGrid/index';
import { IGridWithFilters } from './type';
import { GridSortModel, GridSelectionModel } from '@mui/x-data-grid';
import * as Yup from 'yup';
import HttpService from '../../../services/HttpService';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';


const TableGridWithFilters = ({
  initialValues,
  filterFields,
  resetFilters,
  validationSchema=Yup.object({}),
  columns,
  hideFilters = false,
  pageSize=15, 
  page=0,
  rowsPerPageOptions=[15, 30, 45],
  paginationMode='client',
  sortingMode='client',
  sortModel: _sortModel = [],
  fetchUrl,
  reFetch,
  responseDataFieldName = 'data',
  responseTotalCountFieldName = 'total_count',
  onFetch, //returns response.data
  onFiltersChange, //returns filters' values,
  onRowsChosen, //returns rows chosen -> its returned as an array of strings, where each string is a Stringified JSON
  componentInBetween=<></>,
  title,
  showCard,
  oneLiner,
  showTitle,
  showDivider,
  resetFiltersTo,
  checkboxSelection=false,
  customSetRows=false,
  //extra explanation: in fetch request 
  ignoreFilters = false, //we make request based on the searchParams, so to avoid duplicate query params, dont append filters state in the query params
  ...rest
}: IGridWithFilters) => {

  const { enqueueSnackbar } = useSnackbar();

  const [filters, setFilters] = useState(initialValues);
  const [rows, setRowsArray] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isFetching, setIsFetching] = useState(true);

  const [sortModel, setSortModel] = useState(_sortModel);

  const [rowSelectionModel, setRowSelectionModel] = useState<GridSelectionModel>([]);

  const { t } = useTranslation();

  const [_pageSize, setPageSize] = useState(paginationMode === 'server'
    ? pageSize
    : undefined);
  const [_page, setPage] = useState(page);
  
  const handleSortModelChange = (newModel: GridSortModel) => {
    setSortModel(newModel);
  };

  const changeFilters = (values: any) => {
    setFilters(createFilterModel(values));
    if (onFiltersChange) {
      onFiltersChange(values);
    }
  };

  const sortingProps = {
    ...(sortingMode === 'server' &&
      {
        sortModel: sortModel,
        onSortModelChange: handleSortModelChange
      }
    )
  };

  const paginationProps = {
    ...(paginationMode === 'server' &&
    {
      fetchingFunction: (pageSize: number, page: number) => {
        setPageSize(pageSize);
        setPage(page);
      },
      setPageSize: setPageSize,
      page: _page,
      setPage: setPage
    })
  };

  const handleRows = async (
    page: number,
    pageSize: number | undefined,
    sortModel: GridSortModel
  ) => {
    try { 
      setIsFetching(true);
      const response = await HttpService.getAxiosClient().get(fetchUrl,
        {
          params: eliminateOjectNotTruthyValues(
            { ...(!ignoreFilters && filters), 
              ...createPaginationModel(page, pageSize),
              ...createOrderModel(sortModel) 
            }),
        });
      if (onFetch) {
        onFetch(response.data, setRowsArray);
      }
      if (!customSetRows) {
        setRowsArray(responseDataFieldName
          ? response.data[responseDataFieldName]
          : response.data);
      }
      setTotalCount(response.data[responseTotalCountFieldName]);
    } catch (e) {
      console.error(e);
      enqueueSnackbar(t('Παρουσιάστηκε σφάλμα, παρακαλούμε προσπαθήστε αργότερα.'), { variant: 'error' });
      setRowsArray([]);
      setTotalCount(0);
    } finally {
      setIsFetching(false);
    }
  };


  const onRowSelection = (newRowSelectionModel: GridSelectionModel) => {
    setRowSelectionModel(newRowSelectionModel);
    onRowsChosen && onRowsChosen(newRowSelectionModel);
  };

  useEffect(() => {
    let active = true;
    (async () => {
      
      handleRows(_page, _pageSize, sortModel);
        
      if (!active) {
        return;
      }
    })();
  
    return () => {
      active = false;
    };
  
  }, [sortModel, filters, _page, _pageSize, reFetch]);

  return (
    <>
      <Box mt={5}>      
        {!hideFilters && <FilterComponentV2
          showDivider={showDivider}
          showTitle={showTitle}
          oneLiner={oneLiner}
          showCard={showCard}
          title={title
            ? title
            : t('Φίλτρα Αναζήτησης')}
          resetFilters={resetFilters}
          initialValues={initialValues}
          filterFields={filterFields}
          changeFilters={changeFilters}
          resetFiltersTo={resetFiltersTo}
          validationSchema={validationSchema}/>
        }
        {componentInBetween}
        <Box sx={{ width: '100%', my: 5 }}>
          <TableGrid
            checkboxSelection={checkboxSelection}
            {...(checkboxSelection && {
              rowSelectionModel: rowSelectionModel,
              onRowSelectionModelChange: (newRowSelectionModel: GridSelectionModel) => {
                onRowSelection(newRowSelectionModel);
              }
            })}
            {...paginationProps}
            {...sortingProps}
            paginationMode={paginationMode}
            rows={rows}
            isFetching={isFetching}
            columns={columns}
            totalCount={totalCount}
            pageSize={paginationMode === 'server'
              ? _pageSize
              : pageSize}
            rowsPerPageOptions={rowsPerPageOptions} 
            {...rest}
          />
        </Box>
      </Box>
    </>
  );
};

export default TableGridWithFilters;